import React, { useRef, useState } from "react";

const MAX_CHARS = 5000;
const COUNTER_THRESHOLD = 1500;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export default function ChatInput({ onSend, disabled }) {
  const [value, setValue] = useState("");
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const remaining = MAX_CHARS - value.length;
  const isOverLimit = value.length > MAX_CHARS;

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      window.alert("Please select an image file.");
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      window.alert("Image must be under 5MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setImage(ev.target?.result ?? null);
      setImageFile(file.name);
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImage(null);
    setImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = () => {
    const trimmed = value.trim();
    if ((!trimmed && !image) || disabled || isOverLimit) return;
    onSend(trimmed || "I have attached a screenshot.", image);
    setValue("");
    clearImage();
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const canSend = (value.trim() || image) && !disabled && !isOverLimit;

  return (
    <div className="chat-input-bar">
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
        {image && (
          <div
            className="chat-input-image-preview"
            style={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 8px",
              background: "#E1F5EE",
              borderRadius: 8,
              marginBottom: 4,
              fontSize: 12,
              color: "#0F6E56",
            }}
          >
            <img
              src={image}
              alt="Attachment preview"
              style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }}
            />
            <span>{imageFile}</span>
            <button
              type="button"
              onClick={clearImage}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#A32D2D",
                fontSize: 16,
                lineHeight: 1,
              }}
              aria-label="Remove attachment"
            >
              ×
            </button>
          </div>
        )}

        <textarea
          ref={textareaRef}
          className="chat-input-textarea"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder="Type a message or attach a screenshot..."
          disabled={disabled}
          rows={1}
          aria-label="Chat message input"
          aria-invalid={isOverLimit}
        />
        {value.length > COUNTER_THRESHOLD && (
          <div
            style={{
              fontSize: 11,
              textAlign: "right",
              color: isOverLimit ? "#A32D2D" : "#888780",
              paddingRight: 4,
            }}
          >
            {isOverLimit
              ? `Message too long — please shorten by ${Math.abs(remaining)} characters`
              : `${remaining} characters remaining`}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImageSelect}
      />
      <button
        className="chat-input-icon-btn"
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        aria-label="Attach screenshot"
        title="Attach screenshot"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
        </svg>
      </button>

      <button
        className="chat-input-send-btn"
        onClick={handleSubmit}
        disabled={!canSend}
        aria-label="Send message"
        type="button"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
        </svg>
      </button>
    </div>
  );
}
